const HTMLElementBase = globalThis.HTMLElement ?? class {}
const COMMENTS_BASE = 'https://www.commoninja.com/api/apps'
const EMAIL_PATTERN =
  /^[a-zA-Z0-9.!#$%&'*+/=?^_`{|}~-]+@[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?(?:\.[a-zA-Z0-9](?:[a-zA-Z0-9-]{0,61}[a-zA-Z0-9])?)*$/u

export function normalizeMessages(payload) {
  if (!payload?.success || !Array.isArray(payload?.data?.items)) return []

  return payload.data.items
    .map((item) => (typeof item?.text === 'string' ? item.text.trim() : ''))
    .filter(Boolean)
    .slice(0, 100)
}

export function validateComment({ username, email, message }) {
  if (!username?.trim()) return 'username'
  if (!email?.trim() || !EMAIL_PATTERN.test(email.trim())) return 'email'
  if (!message?.trim()) return 'message'
  if (message.trim().length > 100) return 'length'
  return null
}

export function getMarqueeDurationSeconds(distance, viewportWidth) {
  const normalizedDistance = Math.max(0, Number(distance) || 0)
  const normalizedViewportWidth = Math.max(1, Number(viewportWidth) || 0)

  return Math.max(10, (normalizedDistance / normalizedViewportWidth) * 10)
}

export async function loadComments(widgetKey, href, { fetchImpl = globalThis.fetch, signal } = {}) {
  if (!widgetKey || typeof fetchImpl !== 'function') return []

  try {
    const query = new URLSearchParams({ limit: '100', page: '1', ref: href })
    const response = await fetchImpl(
      `${COMMENTS_BASE}/comments/get-comments?widgetId=${encodeURIComponent(widgetKey)}&${query}`,
      { signal },
    )

    if (!response.ok) return []
    return normalizeMessages(await response.json())
  } catch (error) {
    if (error?.name !== 'AbortError') {
      console.warn('[anniversary-comments] Unable to load messages.')
    }
    return []
  }
}

export async function submitComment(
  widgetKey,
  { username, email, message },
  {
    fetchImpl = globalThis.fetch,
    signal,
    href = globalThis.location?.href ?? '',
    pathname = globalThis.location?.pathname ?? '',
    hostname = globalThis.location?.hostname ?? '',
  } = {},
) {
  if (!widgetKey || typeof fetchImpl !== 'function') throw new Error('comment-submit-unavailable')

  const body = new URLSearchParams({
    widgetId: widgetKey,
    text: message.trim(),
    username: username.trim(),
    email: email.trim(),
    parentCommentId: '',
    serviceName: 'commentsninja',
    pathName: pathname,
    hostName: hostname,
    captchaToken: '',
  })
  const response = await fetchImpl(
    `${COMMENTS_BASE}/comments/create-comment?ref=${encodeURIComponent(href)}`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8' },
      body,
      signal,
    },
  )
  const payload = await response.json().catch(() => ({}))

  if (!response.ok || !payload?.success) throw new Error('comment-submit-failed')
  return payload
}

export class AnniversaryComments extends HTMLElementBase {
  connectedCallback() {
    this.disconnectedCallback()
    this._abortController = new AbortController()
    this._form = this.querySelector('[data-anniversary-comments-form]')
    this._status = this.querySelector('[data-anniversary-comments-status]')
    this._marquee = this.querySelector('[data-anniversary-comments-marquee]')
    this._rows = Array.from(this.querySelectorAll('[data-anniversary-comments-row]'))
    this._accessibleList = this.querySelector('[data-anniversary-comments-accessible-list]')
    this._onSubmit = this._handleSubmit.bind(this)
    this._onResize = this._syncAnimationDurations.bind(this)

    this._form?.addEventListener('submit', this._onSubmit, {
      signal: this._abortController.signal,
    })
    globalThis.addEventListener?.('resize', this._onResize, {
      signal: this._abortController.signal,
    })

    this._load()
  }

  disconnectedCallback() {
    this._abortController?.abort()
    this._abortController = null
    this._form = null
    this._status = null
    this._marquee = null
    this._rows = []
    this._accessibleList = null
    this._onResize = null
  }

  async _load() {
    const requestSignal = this._abortController?.signal
    const messages = await loadComments(
      this.dataset.widgetKey ?? '',
      globalThis.location?.href ?? '',
      {
        signal: requestSignal,
      },
    )

    if (
      !this.isConnected ||
      requestSignal?.aborted ||
      requestSignal !== this._abortController?.signal
    ) {
      return
    }
    this._renderMessages(messages)
  }

  _renderMessages(messages) {
    const japaneseMessages = new Map([
      ['Happy birthday!', '9周年おめでとうございます！'],
      ['Happy birthday! 🎂', '9周年おめでとうございます！🎂'],
      ['Cheers to 9 years of making!', 'ものづくりの9年間に乾杯！'],
      ['Happy birthday, LaserPecker!', 'LaserPecker、9周年おめでとう！'],
      ['Happy 9th!', '祝・9周年！'],
    ])
    messages = messages.map((message) => japaneseMessages.get(message) ?? message)

    if (this._marquee) this._marquee.hidden = messages.length === 0

    this._accessibleList?.replaceChildren(
      ...messages.map((message) => {
        const item = globalThis.document.createElement('li')
        item.textContent = message
        return item
      }),
    )

    for (const [rowIndex, row] of this._rows.entries()) {
      row.replaceChildren()

      if (messages.length === 0) {
        row.hidden = true
        continue
      }

      row.hidden = false
      const rowMessages = Array.from(
        { length: Math.max(5, messages.length) },
        (_, index) => messages[(index + rowIndex) % messages.length],
      )

      for (const message of [...rowMessages, ...rowMessages]) {
        const chip = globalThis.document.createElement('span')
        chip.className = 'anniversary-comments__chip'
        chip.textContent = message
        row.append(chip)
      }
    }

    this._syncAnimationDurations()
  }

  _syncAnimationDurations() {
    const viewportWidth = Math.max(this._marquee?.clientWidth || 0, 1)

    for (const row of this._rows) {
      const repeatStartIndex = Math.floor(row.children.length / 2)
      const firstChip = row.children[0]
      const repeatStartChip = row.children[repeatStartIndex]

      if (!firstChip || !repeatStartChip) continue

      const distance = repeatStartChip.offsetLeft - firstChip.offsetLeft
      row.style.setProperty('--anniversary-comments-distance', `${-distance}px`)
      row.style.setProperty(
        '--anniversary-comments-duration',
        `${getMarqueeDurationSeconds(distance, viewportWidth)}s`,
      )
    }
  }

  async _handleSubmit(event) {
    event.preventDefault()
    if (!this._form || this._form.dataset.submitting === 'true') return

    const fields = {
      username: this._form.elements.username?.value ?? '',
      email: this._form.elements.email?.value ?? '',
      message: this._form.elements.message?.value ?? '',
    }
    const invalid = validateComment(fields)

    this._form
      .querySelectorAll('[aria-invalid="true"]')
      .forEach((field) => field.removeAttribute('aria-invalid'))

    if (invalid) {
      const fieldName = invalid === 'length' ? 'message' : invalid
      this._form.elements[fieldName]?.setAttribute('aria-invalid', 'true')
      this._form.elements[fieldName]?.focus()
      this._setStatus(
        this.dataset[`validation${invalid[0].toUpperCase()}${invalid.slice(1)}`],
        true,
      )
      return
    }

    if (!this.dataset.widgetKey) {
      this._setStatus(this.dataset.errorText, true)
      return
    }

    this._form.dataset.submitting = 'true'
    const button = this._form.querySelector('button[type="submit"]')
    button?.setAttribute('aria-busy', 'true')
    button?.setAttribute('disabled', '')
    this._setStatus(this.dataset.submittingText, false)

    try {
      await submitComment(this.dataset.widgetKey, fields, {
        signal: this._abortController?.signal,
      })
      if (!this.isConnected) return

      this._form.reset()
      this._setStatus(this.dataset.successText, false)
      await this._load()
    } catch (error) {
      if (error?.name !== 'AbortError' && this.isConnected) {
        this._setStatus(this.dataset.errorText, true)
      }
    } finally {
      if (this.isConnected) {
        delete this._form.dataset.submitting
        button?.removeAttribute('aria-busy')
        button?.removeAttribute('disabled')
      }
    }
  }

  _setStatus(message, isError) {
    if (!this._status) return

    this._status.textContent = message ?? ''
    this._status.dataset.status = isError ? 'error' : 'notice'
  }
}

if (globalThis.customElements && !globalThis.customElements.get('anniversary-comments')) {
  globalThis.customElements.define('anniversary-comments', AnniversaryComments)
}
